import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { customerAPI, ordersAPI } from "../../utils/api";
import { useSearchParams } from "react-router-dom";
import {
  MapPin,
  Clock,
  CheckCircle,
  Package,
  Truck,
  AlertCircle,
  Search,
  Loader2,
  Navigation,
  Home,
  User,
  Phone,
  DollarSign,
  Weight,
  FileText,
  Star,
  ChevronRight,
  Calendar,
  XCircle,
} from "lucide-react";
import { format } from "date-fns";

const formatMoney = (val) => {
  const num =
    typeof val === "string"
      ? parseFloat(val)
      : typeof val === "number"
        ? val
        : 0;
  return isNaN(num) ? "0.00" : num.toFixed(2);
};

const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  try {
    return format(new Date(dateString), "MMM d, yyyy h:mm a");
  } catch {
    return "Invalid Date";
  }
};

const statusSteps = [
  {
    id: "pending",
    label: "Order Placed",
    icon: FileText,
    description: "Your order has been received",
  },
  {
    id: "assigned",
    label: "Courier Assigned",
    icon: Truck,
    description: "A courier has been assigned",
  },
  {
    id: "picked_up",
    label: "Picked Up",
    icon: Package,
    description: "Package collected from sender",
  },
  {
    id: "in_transit",
    label: "In Transit",
    icon: Navigation,
    description: "Package is on the way",
  },
  {
    id: "delivered",
    label: "Delivered",
    icon: CheckCircle,
    description: "Package delivered successfully",
  },
];

const statusConfig = {
  pending: {
    color: "bg-amber-100 text-amber-800 border-amber-200",
    icon: Clock,
  },
  assigned: { color: "bg-blue-100 text-blue-800 border-blue-200", icon: Truck },
  picked_up: {
    color: "bg-violet-100 text-violet-800 border-violet-200",
    icon: Package,
  },
  in_transit: {
    color: "bg-cyan-100 text-cyan-800 border-cyan-200",
    icon: Navigation,
  },
  delivered: {
    color: "bg-emerald-100 text-emerald-800 border-emerald-200",
    icon: CheckCircle,
  },
  failed: { color: "bg-rose-100 text-rose-800 border-rose-200", icon: XCircle },
  cancelled: {
    color: "bg-gray-100 text-gray-800 border-gray-200",
    icon: AlertCircle,
  },
};

const CustomerTracking = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [trackingNumber, setTrackingNumber] = useState(
    searchParams.get("orderId") || "",
  );
  const [searchedOrder, setSearchedOrder] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  const {
    data: trackingData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["tracking", trackingNumber],
    queryFn: () => ordersAPI.tracking(trackingNumber),
    enabled: !!trackingNumber && trackingNumber.length > 0,
    retry: 1,
  });

  const handleSearch = (e) => {
    e.preventDefault();
    if (!trackingNumber.trim()) return;
    setSearchParams({ orderId: trackingNumber });
    setSearchedOrder(null);
    refetch();
  };

  const order = trackingData?.data?.data;
  const tracking = order?.tracking || [];
  const currentStatus = order?.status || "pending";
  const currentStepIndex = statusSteps.findIndex((s) => s.id === currentStatus);

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="text-center">
        <h1 className="text-3xl font-bold text-gray-900">Track Your Order</h1>
        <p className="text-gray-500 mt-2">
          Enter your tracking number to see real-time status
        </p>
      </div>

      {/* Search */}
      <form onSubmit={handleSearch} className="card">
        <div className="relative max-w-xl mx-auto">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            value={trackingNumber}
            onChange={(e) => setTrackingNumber(e.target.value)}
            className="input-field pl-12 pr-32 py-4 text-lg w-full"
            placeholder="Enter tracking number (e.g., TRK-123456789)"
          />
          <button
            type="submit"
            disabled={isLoading || !trackingNumber.trim()}
            className="absolute right-2 top-1/2 -translate-y-1/2 btn-primary py-2.5 px-6 disabled:opacity-50"
          >
            {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Track"}
          </button>
        </div>
      </form>

      {/* Loading */}
      {isLoading && (
        <div className="card flex flex-col items-center justify-center py-16">
          <Loader2 className="w-12 h-12 animate-spin text-primary-600 mb-4" />
          <p className="text-gray-500 font-medium">Tracking your order...</p>
        </div>
      )}

      {/* Error */}
      {error && !isLoading && (
        <div className="card flex flex-col items-center justify-center py-16 text-gray-500">
          <AlertCircle className="w-12 h-12 text-red-400 mb-4" />
          <p className="font-medium text-lg">Order not found</p>
          <p className="text-sm mt-1">
            Please check your tracking number and try again
          </p>
        </div>
      )}

      {/* Results */}
      {order && !isLoading && (
        <div className="space-y-6">
          {/* Status Header */}
          <div className="card bg-gradient-to-br from-primary-50 to-primary-100/50 border-primary-200">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex items-center space-x-4">
                <div
                  className={`p-3 rounded-xl ${statusConfig[currentStatus]?.color || "bg-gray-100"}`}
                >
                  {(() => {
                    const Icon = statusConfig[currentStatus]?.icon || Clock;
                    return <Icon className="w-8 h-8" />;
                  })()}
                </div>
                <div>
                  <p className="text-sm text-gray-500">Order Status</p>
                  <p className="text-2xl font-bold text-gray-900 capitalize">
                    {currentStatus.replace("_", " ")}
                  </p>
                  <p className="text-sm text-gray-600 mt-0.5">
                    {statusSteps.find((s) => s.id === currentStatus)
                      ?.description || "Processing your order"}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="font-mono text-lg font-semibold text-gray-900 bg-white px-3 py-1.5 rounded-lg border border-gray-200">
                  {order.tracking_number}
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  {formatDate(order.created_at)}
                </p>
              </div>
            </div>
          </div>

          {/* Progress Timeline */}
          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-6">
              Delivery Progress
            </h3>
            <div className="relative">
              {/* Progress Line */}
              <div className="absolute top-5 left-0 right-0 h-1 bg-gray-200 rounded-full">
                <div
                  className="h-full bg-primary-500 rounded-full transition-all duration-500"
                  style={{
                    width: `${Math.max(0, (currentStepIndex / (statusSteps.length - 1)) * 100)}%`,
                  }}
                />
              </div>

              <div className="relative grid grid-cols-5 gap-2">
                {statusSteps.map((step, index) => {
                  const Icon = step.icon;
                  const isCompleted = index <= currentStepIndex;
                  const isCurrent = index === currentStepIndex;

                  return (
                    <div
                      key={step.id}
                      className="flex flex-col items-center text-center"
                    >
                      <div
                        className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all z-10 ${
                          isCompleted
                            ? "bg-primary-600 border-primary-600 text-white"
                            : "bg-white border-gray-300 text-gray-400"
                        } ${isCurrent ? "ring-4 ring-primary-100" : ""}`}
                      >
                        <Icon className="w-5 h-5" />
                      </div>
                      <p
                        className={`text-xs font-semibold mt-2 ${isCompleted ? "text-gray-900" : "text-gray-400"}`}
                      >
                        {step.label}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5 hidden sm:block">
                        {step.description}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Order Details & Map */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Details */}
            <div className="card">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  <Package className="w-5 h-5 mr-2 text-blue-500" />
                  Order Details
                </h3>
                <button
                  onClick={() => setShowDetails(!showDetails)}
                  className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center"
                >
                  {showDetails ? "Hide" : "Show All"}
                  <ChevronRight
                    className={`w-4 h-4 ml-1 transition-transform ${showDetails ? "rotate-90" : ""}`}
                  />
                </button>
              </div>

              <div className="space-y-4">
                <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-xl">
                  <Home className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs text-gray-500 uppercase font-medium">
                      Pickup
                    </p>
                    <p className="text-sm font-medium text-gray-900 mt-0.5">
                      {order.pickup_address}
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-xl">
                  <MapPin className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs text-gray-500 uppercase font-medium">
                      Delivery
                    </p>
                    <p className="text-sm font-medium text-gray-900 mt-0.5">
                      {order.delivery_address}
                    </p>
                    <div className="flex items-center mt-2 text-sm text-gray-600">
                      <User className="w-3.5 h-3.5 mr-1" />
                      {order.recipient_name}
                      <Phone className="w-3.5 h-3.5 ml-3 mr-1" />
                      {order.recipient_phone}
                    </div>
                  </div>
                </div>

                {showDetails && (
                  <div className="space-y-3 pt-2">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <p className="text-xs text-gray-500">Package Type</p>
                        <p className="font-medium text-gray-900 capitalize">
                          {order.package_type}
                        </p>
                      </div>
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <p className="text-xs text-gray-500">Weight</p>
                        <p className="font-medium text-gray-900">
                          {order.weight} kg
                        </p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <p className="text-xs text-gray-500">Delivery Type</p>
                        <p className="font-medium text-gray-900 capitalize">
                          {order.delivery_type?.replace("_", " ")}
                        </p>
                      </div>
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <p className="text-xs text-gray-500">Payment</p>
                        <p className="font-medium text-gray-900 capitalize">
                          {order.payment_method}
                        </p>
                      </div>
                    </div>
                    {order.notes && (
                      <div className="p-3 bg-amber-50 rounded-lg border border-amber-100">
                        <p className="text-xs text-amber-700 font-medium">
                          Notes
                        </p>
                        <p className="text-sm text-amber-900 mt-1">
                          {order.notes}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                <div className="border-t border-gray-200 pt-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Subtotal</span>
                    <span className="font-medium">
                      EGP {formatMoney(order.subtotal)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm mt-2">
                    <span className="text-gray-500">Delivery Fee</span>
                    <span className="font-medium">
                      EGP {formatMoney(order.delivery_fee)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm mt-2">
                    <span className="text-gray-500">Tax</span>
                    <span className="font-medium">
                      EGP {formatMoney(order.tax)}
                    </span>
                  </div>
                  <div className="flex justify-between text-base font-bold mt-3 pt-3 border-t border-gray-200">
                    <span>Total</span>
                    <span className="text-primary-600">
                      EGP {formatMoney(order.total_amount)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Tracking History */}
            <div className="card">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Clock className="w-5 h-5 mr-2 text-blue-500" />
                Tracking History
              </h3>
              <div className="space-y-4 max-h-[500px] overflow-y-auto pr-2">
                {tracking.length === 0 ? (
                  <div className="text-center py-8 text-gray-400">
                    <Clock className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>No tracking updates yet</p>
                  </div>
                ) : (
                  tracking.map((event, index) => {
                    const isLatest = index === 0;
                    const EventIcon = statusConfig[event.status]?.icon || Clock;
                    return (
                      <div
                        key={index}
                        className={`flex items-start space-x-3 p-3 rounded-xl transition-colors ${isLatest ? "bg-primary-50 border border-primary-100" : "hover:bg-gray-50"}`}
                      >
                        <div
                          className={`p-2 rounded-lg flex-shrink-0 ${isLatest ? "bg-primary-100 text-primary-600" : "bg-gray-100 text-gray-400"}`}
                        >
                          <EventIcon className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <p
                              className={`text-sm font-medium ${isLatest ? "text-primary-900" : "text-gray-900"}`}
                            >
                              {event.status?.replace("_", " ")?.toUpperCase()}
                            </p>
                            {isLatest && (
                              <span className="text-xs bg-primary-100 text-primary-700 px-2 py-0.5 rounded-full font-medium">
                                Latest
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600 mt-0.5">
                            {event.location || "Location unknown"}
                          </p>
                          {event.notes && (
                            <p className="text-xs text-gray-500 mt-1">
                              {event.notes}
                            </p>
                          )}
                          <p className="text-xs text-gray-400 mt-1">
                            {formatDate(event.created_at)}
                          </p>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>

          {/* Rating (if delivered) */}
          {currentStatus === "delivered" && (
            <div className="card bg-emerald-50 border-emerald-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-emerald-100 rounded-lg">
                    <Star className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-emerald-900">
                      Your order has been delivered!
                    </p>
                    <p className="text-sm text-emerald-700">
                      How was your delivery experience?
                    </p>
                  </div>
                </div>
                <div className="flex space-x-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      className="p-1 hover:scale-110 transition-transform"
                    >
                      <Star className="w-8 h-8 text-gray-300 hover:text-amber-400 fill-current" />
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CustomerTracking;
